import {
  FileText,
  Palette,
  Download,
  Sparkles,
  User,
  Briefcase,
  GraduationCap,
  Award,
  FileCheck,
  LayoutTemplate,
  PenLine,
  ChevronRight,
  Mail,
  Cloud,
} from 'lucide-react';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ResumeData, OptimizationResult, BaselineScores } from '../AIResumeOptimizer/shared/types';
import { parseResumeSections } from '../AIResumeOptimizer/utils/parseResumeSections';

export type BuilderMode = 'fill-in' | 'design' | 'download-share' | 'improve';

const ATS_TEMPLATE_META = [
  { id: 'professional', icon: FileCheck },
  { id: 'minimal', icon: LayoutTemplate },
  { id: 'modern', icon: PenLine },
] as const;

const FILL_IN_SECTION_META = [
  { id: 'personal', icon: User },
  { id: 'education', icon: GraduationCap },
  { id: 'work', icon: Briefcase },
  { id: 'skills', icon: FileText },
  { id: 'awards', icon: Award },
] as const;

interface ResumeBuilderViewProps {
  onBack: () => void;
  onNavigate?: (page: string) => void;
  /** Pre-selected template when opening from My Activities (e.g. "professional"). */
  initialTemplateId?: string;
  /** Resume content to edit; can come from upload or optimization. */
  resumeData?: ResumeData | null;
  /** Optimized resume result; used for preview and download when available. */
  optimizationResult?: OptimizationResult | null;
  /** Called when user requests download (format). */
  onDownload?: (format: 'pdf' | 'docx' | 'txt') => void;
}

export const ResumeBuilderView: React.FC<ResumeBuilderViewProps> = ({
  onBack,
  onNavigate,
  initialTemplateId = 'professional',
  resumeData,
  optimizationResult,
  onDownload,
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<BuilderMode>('fill-in');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(initialTemplateId || 'professional');

  const fillInSectionsBase = useMemo(() => FILL_IN_SECTION_META.map((sec) => {
    const labels: Record<string, string> = {
      personal: t('dashboard.resumeBuilder.sections.personal', 'Personal Information'),
      education: t('dashboard.resumeBuilder.sections.education', 'Education'),
      work: t('dashboard.resumeBuilder.sections.work', 'Work Experience'),
      skills: t('dashboard.resumeBuilder.sections.skills', 'Skills'),
      awards: t('dashboard.resumeBuilder.sections.awards', 'Awards & Certifications'),
    };
    return { ...sec, label: labels[sec.id] };
  }), [t]);

  const atsTemplates = useMemo(() => ATS_TEMPLATE_META.map((tpl) => {
    const names: Record<string, string> = {
      professional: t('dashboard.resumeBuilder.templates.professional.name', 'Professional'),
      minimal: t('dashboard.resumeBuilder.templates.minimal.name', 'Minimal'),
      modern: t('dashboard.resumeBuilder.templates.modern.name', 'Modern'),
    };
    const descs: Record<string, string> = {
      professional: t('dashboard.resumeBuilder.templates.professional.desc', 'Clean sections, clear headings. Best for ATS and recruiters.'),
      minimal: t('dashboard.resumeBuilder.templates.minimal.desc', 'Simple layout, maximum readability. Strong ATS compatibility.'),
      modern: t('dashboard.resumeBuilder.templates.modern.desc', 'Contemporary style with clear hierarchy. ATS-friendly structure.'),
    };
    return { ...tpl, name: names[tpl.id], description: descs[tpl.id] };
  }), [t]);

  const previewContent = optimizationResult?.optimizedResume ?? resumeData?.parsedText ?? t('dashboard.resumeBuilder.previewEmpty', 'Upload or paste your resume to see a live preview here.');
  const sections = useMemo(() => {
    const text = optimizationResult?.optimizedResume ?? resumeData?.parsedText ?? '';
    if (!text.trim()) return fillInSectionsBase.map((s) => ({ ...s, content: '', preview: '' }));
    const parsed = parseResumeSections(text);
    return fillInSectionsBase.map((sec) => {
      const found = parsed.find((p) => p.title.toLowerCase().includes(sec.id) || sec.label.toLowerCase().includes(p.title.toLowerCase()));
      return {
        ...sec,
        content: found?.content ?? '',
        preview: found?.preview ?? '',
      };
    });
  }, [optimizationResult?.optimizedResume, resumeData?.parsedText, fillInSectionsBase]);

  const baselineScores: BaselineScores | null = optimizationResult?.afterScores ?? optimizationResult?.beforeScores ?? null;
  const atsScore = baselineScores ? Math.round((baselineScores.formatting + baselineScores.keywords + baselineScores.structure) / 3) : null;

  const sidebarItems: { id: BuilderMode; label: string; icon: React.ElementType }[] = useMemo(() => [
    { id: 'fill-in', label: t('dashboard.resumeBuilder.modeFillIn', 'Fill In'), icon: FileText },
    { id: 'design', label: t('dashboard.resumeBuilder.modeDesign', 'Design'), icon: Palette },
    { id: 'download-share', label: t('dashboard.resumeBuilder.modeDownload', 'Download & Share'), icon: Download },
    { id: 'improve', label: t('dashboard.resumeBuilder.modeImprove', 'Improve'), icon: Sparkles },
  ], [t]);

  return (
    <div className="flex flex-col h-full min-h-0 bg-[var(--vscode-editor-background)]" style={{ pointerEvents: 'auto' }}>
      {/* Top bar: Back + title */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--vscode-panel-border)] bg-[var(--vscode-editor-background)] shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-[var(--vscode-list-hoverBackground)] text-[var(--vscode-foreground)] touch-manipulation"
          aria-label={t('dashboard.resumeBuilder.back', 'Back')}
        >
          ← {t('dashboard.resumeBuilder.back', 'Back')}
        </button>
        <h1 className="text-lg font-semibold text-[var(--vscode-foreground)]">{t('dashboard.resumeBuilder.title', 'Resume Builder')}</h1>
      </div>

      {/* Mobile tab bar — visible below md */}
      <nav
        className="md:hidden flex shrink-0 overflow-x-auto border-b border-[var(--vscode-panel-border)] bg-[var(--vscode-sideBar-background)]"
        aria-label={t('dashboard.resumeBuilder.sectionsNav', 'Builder sections')}
      >
        {sidebarItems.map((item) => {
          const Icon = item.icon;
          const active = mode === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setMode(item.id)}
              className={`flex items-center gap-1.5 whitespace-nowrap min-h-[44px] px-3 text-xs font-medium transition-colors touch-manipulation ${
                active
                  ? 'border-b-2 border-[var(--vscode-focusBorder)] text-[var(--vscode-list-activeSelectionForeground)]'
                  : 'text-[var(--vscode-foreground)] hover:bg-[var(--vscode-list-hoverBackground)]'
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="flex flex-col md:flex-row flex-1 min-h-0">
        {/* Left sidebar: Fill In | Design | Download & Share | Improve — hidden on mobile (tab bar replaces it) */}
        <nav
          className="hidden md:flex w-52 shrink-0 border-r border-[var(--vscode-panel-border)] bg-[var(--vscode-sideBar-background)] flex-col py-2"
          aria-label={t('dashboard.resumeBuilder.sectionsNav', 'Builder sections')}
        >
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            const active = mode === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setMode(item.id)}
                className={`flex items-center gap-3 w-full px-4 py-3 text-left text-sm transition-colors ${
                  active
                    ? 'bg-[var(--vscode-list-activeSelectionBackground)] text-[var(--vscode-list-activeSelectionForeground)]'
                    : 'text-[var(--vscode-foreground)] hover:bg-[var(--vscode-list-hoverBackground)]'
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Main: left content + right preview */}
        <div className="flex flex-col md:flex-row flex-1 min-w-0">
          {/* Left column: content for current mode */}
          <div className="w-full md:w-[380px] md:shrink-0 border-b md:border-b-0 md:border-r border-[var(--vscode-panel-border)] overflow-y-auto bg-[var(--vscode-editor-background)]">
            {mode === 'fill-in' && (
              <div className="p-4 space-y-2">
                <p className="text-sm text-[var(--vscode-descriptionForeground)] mb-4">
                  {t('dashboard.resumeBuilder.fillInHint', 'Your resume is shown in sections. Edit each section; the preview updates on the right.')}
                </p>
                {sections.map((sec) => {
                  const Icon = sec.icon;
                  return (
                    <div
                      key={sec.id}
                      className="rounded-lg border border-[var(--vscode-panel-border)] bg-[var(--vscode-input-background)] p-3 flex items-start gap-3"
                    >
                      <Icon className="w-5 h-5 text-[var(--vscode-foreground)] shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-[var(--vscode-foreground)]">{sec.label}</div>
                        <p className="text-xs text-[var(--vscode-descriptionForeground)] mt-1 line-clamp-2">
                          {sec.preview || t('dashboard.resumeBuilder.noContent', 'No content yet')}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[var(--vscode-descriptionForeground)] shrink-0" />
                    </div>
                  );
                })}
                <p className="text-xs text-[var(--vscode-descriptionForeground)] pt-2">
                  {t('dashboard.resumeBuilder.optimizeHint', 'Start with Single Optimize to upload your resume and fill sections automatically.')}
                </p>
              </div>
            )}

            {mode === 'design' && (
              <div className="p-4 space-y-4">
                <p className="text-sm text-[var(--vscode-descriptionForeground)]">
                  {t('dashboard.resumeBuilder.designHint', 'ATS-compliant templates. Select one to see the change in the preview.')}
                </p>
                <div className="space-y-3">
                  {atsTemplates.map((tpl) => {
                    const Icon = tpl.icon;
                    const selected = selectedTemplateId === tpl.id;
                    return (
                      <button
                        key={tpl.id}
                        type="button"
                        onClick={() => setSelectedTemplateId(tpl.id)}
                        className={`w-full rounded-lg border-2 p-4 text-left transition-colors ${
                          selected
                            ? 'border-[var(--vscode-focusBorder)] bg-[var(--vscode-list-activeSelectionBackground)]'
                            : 'border-[var(--vscode-panel-border)] hover:border-[var(--vscode-focusBorder)]'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Icon className="w-5 h-5 text-[var(--vscode-foreground)]" />
                          <span className="font-medium text-[var(--vscode-foreground)]">{tpl.name}</span>
                        </div>
                        <p className="text-xs text-[var(--vscode-descriptionForeground)] mt-2">{tpl.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {mode === 'download-share' && (
              <div className="p-4 space-y-4">
                <p className="text-sm text-[var(--vscode-descriptionForeground)]">{t('dashboard.resumeBuilder.downloadHint', 'Download or share your resume.')}</p>
                <div className="space-y-2">
                  {(['pdf', 'docx', 'txt'] as const).map((format) => (
                    <button
                      key={format}
                      type="button"
                      onClick={() => onDownload?.(format)}
                      className="w-full flex items-center gap-3 rounded-lg border border-[var(--vscode-panel-border)] p-3 hover:bg-[var(--vscode-list-hoverBackground)] text-[var(--vscode-foreground)]"
                    >
                      <Download className="w-5 h-5" />
                      <span className="font-medium">{t('dashboard.resumeBuilder.downloadAs', { defaultValue: 'Download as {{format}}', format: format.toUpperCase() })}</span>
                    </button>
                  ))}
                </div>
                <div className="pt-4 border-t border-[var(--vscode-panel-border)]">
                  <p className="text-xs text-[var(--vscode-descriptionForeground)] mb-2">{t('dashboard.resumeBuilder.share', 'Share')}</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--vscode-panel-border)] hover:bg-[var(--vscode-list-hoverBackground)] text-[var(--vscode-foreground)]"
                    >
                      <Mail className="w-4 h-4" />
                      <span className="text-sm">{t('dashboard.resumeBuilder.email', 'Email')}</span>
                    </button>
                    <button
                      type="button"
                      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--vscode-panel-border)] hover:bg-[var(--vscode-list-hoverBackground)] text-[var(--vscode-foreground)]"
                    >
                      <Cloud className="w-4 h-4" />
                      <span className="text-sm">{t('dashboard.resumeBuilder.saveToCloud', 'Save to cloud')}</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {mode === 'improve' && (
              <div className="p-4 space-y-4">
                {atsScore != null && (
                  <div className="rounded-lg border border-[var(--vscode-panel-border)] p-4">
                    <h3 className="font-medium text-[var(--vscode-foreground)]">{t('dashboard.resumeBuilder.atsScore', 'ATS Score')}</h3>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="h-2 flex-1 rounded-full bg-[var(--vscode-input-background)] overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[var(--vscode-progressBar-background)]"
                          style={{ width: `${atsScore}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-[var(--vscode-foreground)]">{atsScore}/100</span>
                    </div>
                    <p className="text-xs text-[var(--vscode-descriptionForeground)] mt-2">
                      {t('dashboard.resumeBuilder.runOptimizationHint', 'Run optimization to get a detailed analysis and improve this score.')}
                    </p>
                    <button
                      type="button"
                      onClick={() => onNavigate?.('agent')}
                      className="mt-2 text-sm text-[var(--vscode-textLink-foreground)] hover:underline flex items-center gap-1"
                    >
                      {t('dashboard.resumeBuilder.seeAnalysis', 'See detailed analysis')} <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {atsScore == null && (
                  <div className="rounded-lg border border-[var(--vscode-panel-border)] p-4">
                    <h3 className="font-medium text-[var(--vscode-foreground)]">{t('dashboard.resumeBuilder.atsScore', 'ATS Score')}</h3>
                    <p className="text-sm text-[var(--vscode-descriptionForeground)] mt-1">
                      {t('dashboard.resumeBuilder.runOptimizeForScore', 'Run Single Optimize to get your ATS score and improvement tips.')}
                    </p>
                    <button
                      type="button"
                      onClick={() => onNavigate?.('agent')}
                      className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium text-white"
                      style={{ backgroundColor: 'var(--vscode-button-background)' }}
                    >
                      <Sparkles className="w-4 h-4" />
                      {t('dashboard.resumeBuilder.improveWithAi', 'Improve with AI')}
                    </button>
                  </div>
                )}
                <div className="rounded-lg border border-[var(--vscode-panel-border)] p-4">
                  <h3 className="font-medium text-[var(--vscode-foreground)]">{t('dashboard.resumeBuilder.recruiterFeedback', 'Recruiter-style feedback')}</h3>
                  <p className="text-xs text-[var(--vscode-descriptionForeground)] mt-1">
                    {t('dashboard.resumeBuilder.recruiterFeedbackHint', 'Get AI feedback on your resume structure and wording. (English only.)')}
                  </p>
                  <button
                    type="button"
                    onClick={() => onNavigate?.('agent')}
                    className="mt-2 text-sm text-[var(--vscode-textLink-foreground)] hover:underline flex items-center gap-1"
                  >
                    {t('dashboard.resumeBuilder.getFeedback', 'Get feedback')} <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right column: preview — collapses below editor on mobile */}
          <div className="flex-1 min-w-0 flex flex-col bg-[var(--vscode-editor-inactiveSelectionBackground)]">
            <div className="px-4 py-2 border-b border-[var(--vscode-panel-border)] flex items-center justify-between">
              <span className="text-sm font-medium text-[var(--vscode-foreground)]">{t('dashboard.resumeBuilder.preview', 'Preview')}</span>
            </div>
            <div className="flex-1 overflow-auto p-4 md:p-6 flex justify-center">
              <div
                className={`resume-preview bg-[var(--vscode-editor-background)] text-[var(--vscode-foreground)] shadow-lg rounded w-full max-w-[21cm] min-h-[50vh] md:min-h-[29.7cm] p-4 md:p-8 ${
                  selectedTemplateId === 'minimal'
                    ? 'font-serif text-sm tracking-tight'
                    : selectedTemplateId === 'modern'
                    ? 'font-sans text-base leading-relaxed'
                    : 'font-sans text-sm leading-relaxed'
                }`}
                style={{ boxShadow: '0 0 20px rgba(0,0,0,0.1)' }}
              >
                <pre className="whitespace-pre-wrap m-0">{previewContent}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumeBuilderView;
